import React, { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Home, Trophy, Target, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuthState";

interface BottomTabNavProps {
  className?: string;
}

interface TabItem {
  path: string;
  icon: React.ComponentType<any>;
  label: string;
  loggedOutLabel?: string;
  loggedOutPath?: string;
  loggedOutIcon?: React.ComponentType<any>;
  matchPaths?: string[];
}

const tabs: TabItem[] = [
  {
    path: "/m",
    icon: Home,
    label: "Dashboard",
    loggedOutLabel: "Home",
    matchPaths: ["/m", "/m/dashboard"]
  },
  {
    path: "/m/competitions",
    icon: Trophy,
    label: "Competitions",
    matchPaths: ["/m/competitions", "/m/competition"]
  },
  {
    path: "/m/challenges",
    icon: Target,
    label: "Challenges",
    matchPaths: ["/m/challenges", "/m/challenge"]
  },
  {
    path: "/m/friends",
    icon: User,
    label: "Friends",
    matchPaths: ["/m/profile", "/m/friends"]
  }
];

export function BottomTabNav({ className }: BottomTabNavProps) {
  const [location, navigate] = useLocation();
  const { user } = useAuth();
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);

  const isActive = (tab: TabItem) => {
    if (tab.matchPaths) {
      return tab.matchPaths.some(path => {
        // Exact match
        if (location === path) return true;
        // Match if location starts with path followed by a slash (e.g., /m/competition/123)
        // but not if it's just a prefix (e.g., /m should not match /m/competitions)
        if (path !== "/m" && location.startsWith(path + "/")) return true;
        return false;
      });
    }
    return location === tab.path;
  };

  const getTabLabel = (tab: TabItem) => {
    // Show loggedOutLabel if user is not logged in and loggedOutLabel exists
    if (!user && tab.loggedOutLabel) {
      return tab.loggedOutLabel;
    }
    return tab.label;
  };

  const getTabPath = (tab: TabItem) => {
    // Use loggedOutPath if user is not logged in and loggedOutPath exists
    if (!user && tab.loggedOutPath) {
      return tab.loggedOutPath;
    }
    return tab.path;
  };

  const getTabIcon = (tab: TabItem) => {
    // Use loggedOutIcon if user is not logged in and loggedOutIcon exists
    if (!user && tab.loggedOutIcon) {
      return tab.loggedOutIcon;
    }
    return tab.icon;
  };

  // Add body class for navbar styling
  React.useEffect(() => {
    document.body.classList.add('mobile-nav-active');
    return () => {
      document.body.classList.remove('mobile-nav-active');
    };
  }, []);

  // Handle scroll to hide/show bottom nav
  useEffect(() => {
    let ticking = false;
    let lastY = lastScrollY;
    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollDifference = Math.abs(currentScrollY - lastY);

          console.log('Scroll event:', { currentScrollY, lastY, scrollDifference, isHidden });

          // Always show bottom nav when at top of page
          if (currentScrollY <= 10) {
            console.log('At top, showing navbar');
            setIsHidden(false);
            setLastScrollY(currentScrollY);
            lastY = currentScrollY;
            ticking = false;
            return;
          }

          // Only react to scroll changes greater than 3px to avoid jitter
          if (scrollDifference > 3) {
            // Scrolling DOWN the page (increasing scroll position) - HIDE bottom nav
            if (currentScrollY > lastY) {
              console.log('Scrolling DOWN - hiding navbar');
              setIsHidden(true);
            }
            // Scrolling UP the page (decreasing scroll position) - SHOW bottom nav
            else if (currentScrollY < lastY) {
              console.log('Scrolling UP - showing navbar');
              setIsHidden(false);
            }

            setLastScrollY(currentScrollY);
            lastY = currentScrollY;
          }

          // Clear existing timeout
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          // Set timeout to show navbar when scrolling stops
          scrollTimeout = setTimeout(() => {
            console.log('Scrolling stopped - showing navbar');
            setIsHidden(false);
          }, 1500); // Show navbar 1.5 seconds after scrolling stops

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, []); // Remove lastScrollY from dependencies to avoid recreation

  return (
    <div
      className={cn(
        "mobile-bottom-nav",
        isHidden ? "translate-y-full" : "translate-y-0"
      )}
      data-testid="bottom-tab-nav"
      style={{
        transform: isHidden ? 'translateY(100%)' : 'translateY(0)'
      }}
    >
      {/* Main 4 tabs in a row */}
      <div className="flex items-center justify-around h-16">
        {tabs.map((tab) => {
          const Icon = getTabIcon(tab);
          const active = isActive(tab);
          const tabPath = getTabPath(tab);
          const tabLabel = getTabLabel(tab);

          return (
            <button
              key={tab.path}
              onClick={() => navigate(tabPath)}
              data-testid={`tab-${tab.label.toLowerCase()}`}
              className={cn(
                "flex flex-col items-center justify-center",
                "min-h-[44px] min-w-[44px] px-2 py-1",
                "rounded-lg transition-all duration-200",
                "touch-manipulation", // Optimize for touch
                active
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              )}
            >
              <Icon
                className={cn(
                  "h-6 w-6 mb-1",
                  active && "scale-110"
                )}
              />
              <span className={cn(
                "text-xs font-medium leading-none",
                active && "text-blue-600 dark:text-blue-400"
              )}>
                {tabLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default BottomTabNav;